/**
 * Engine hours in a form: a field that takes "1234,5" or "1234.5" (up to two decimals), says what
 * is wrong with a half-written or too exact value, and a prompt for the warning the server gives
 * when a reading gains more hours than the time that has passed.
 */

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { parseHours } from "@common/hours";
import { ApiError } from "../services/api-error";
import { localDay, stockholmTime } from "./time";

export type HoursRead = { kind: "empty" } | { kind: "ok"; value: number } | { kind: "bad"; reason: "format" | "decimals" | "range" };

/** The text of a field as hours: empty is unknown (never zero). */
export function readHours(text: string): HoursRead {
  const p = parseHours(text);
  if (p.ok) return { kind: "ok", value: p.value };
  return p.reason === "empty" ? { kind: "empty" } : { kind: "bad", reason: p.reason };
}

/** The value of a field that is empty or valid; undefined for both (use readHours to tell them apart). */
export const hoursValue = (text: string): number | undefined => {
  const r = readHours(text);
  return r.kind === "ok" ? r.value : undefined;
};

export function HoursInput({ label, value, onChange, hint, id }: { label: string; value: string; onChange: (v: string) => void; hint?: string; id?: string }) {
  const { t } = useTranslation();
  const read = readHours(value);
  return (
    <label className="block text-sm" htmlFor={id}>
      {label}
      <input id={id} aria-label={label} className="input mt-1 text-lg" inputMode="decimal" autoComplete="off" value={value} onChange={(e) => onChange(e.target.value)} aria-invalid={read.kind === "bad"} />
      {hint && <span className="text-xs text-gray-500">{hint}</span>}
      {read.kind === "bad" && <span className="block text-xs text-red-600">{t(`logbook.hours.${read.reason}`)}</span>}
    </label>
  );
}

export interface HoursConfirmation {
  code: "hours_jump";
  previousEntryId: string;
  reason: string;
}

/** The warning from the server that a reading gained more hours than the time that passed, if this error is one. */
export function hoursJumpOf(error: unknown): { increase: number; elapsedHours: number; engineId?: string; engineName?: string; previous: { entryId: string; total: number; at: string } } | null {
  if (!(error instanceof ApiError) || error.code !== "hours_jump") return null;
  const d = error.details as { increase: number; elapsedHours: number; previous: { entryId: string; total: number; at: string } } | undefined;
  return d ?? null;
}

const fmt = (n: number) => String(Math.round(n * 100) / 100).replace(".", ",");

/** Shows the warning with what was compared, and lets the skipper change the reading or save it with an explanation. */
export function HoursJumpPrompt({ error, onChange, onConfirm, busy }: { error: unknown; onChange: () => void; onConfirm: (c: HoursConfirmation) => void; busy?: boolean }) {
  const { t } = useTranslation();
  const [reason, setReason] = useState("");
  const jump = hoursJumpOf(error);
  if (!jump) return null;
  return (
    <div role="alert" className="space-y-2 rounded border border-amber-300 bg-amber-50 p-3 text-sm text-amber-950">
      {jump.engineName && <p className="font-semibold">{jump.engineName}</p>}
      <p className="font-medium">{t("logbook.hours.jump", { increase: fmt(jump.increase), elapsed: fmt(jump.elapsedHours) })}</p>
      <p className="text-xs">{t("logbook.hours.jumpCompared", { previous: fmt(jump.previous.total), at: `${localDay(jump.previous.at)} ${stockholmTime(jump.previous.at)}` })}</p>
      <label className="block">
        {t("logbook.hours.reason")}
        <input className="input mt-1" value={reason} onChange={(e) => setReason(e.target.value)} />
      </label>
      <div className="flex gap-2">
        <button type="button" className="btn btn-secondary" onClick={onChange}>
          {t("logbook.hours.change")}
        </button>
        <button type="button" className="btn btn-primary" disabled={busy || reason.trim().length < 3} onClick={() => onConfirm({ code: "hours_jump", previousEntryId: jump.previous.entryId, reason: reason.trim() })}>
          {t("logbook.hours.saveWithReason")}
        </button>
      </div>
    </div>
  );
}
